import { makeAutoObservable, runInAction } from 'mobx'
import { type ReactNode } from 'react'
import dayjs from 'dayjs'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { DayjsFormats, logger } from 'shared/lib'
import { sizeAlertCreator } from 'shared/lib/sizeAlertCreator'
import {
  AttachmentFetchType,
  AttachmentsApi,
  type ILimitSizeError,
  type IResponseEventAttachmentUploadError,
  type IResponseMedia,
  type IResponseMediaShort,
} from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RecordAudioStore } from 'widgets/RecordAudio/store/recordAudioStore'
import { RenameModalStore } from 'widgets/RenameModal'
import { TextToSpeechModalContent, TextToSpeechWidgetStore } from 'widgets/TextToSpeech'
import { type IRinglessMessageDataParams } from 'widgets/MessageFormFields/RinglessMessageField/store/type'
import { type ICreateTextToSpeechStoreProps } from 'widgets/TextToSpeech/store/textToSpeechWidgetStore'
import type { IMediaLibraryStoreProps } from 'widgets/MediaLibrary/types/types'
import { RinglessRecordAudioModal } from 'widgets/MessageFormFields/RinglessMessageField/ui/RecordAudioModal/RecordAudioModal'

type IRinglessMessageFieldStoreProps = {
  clearParentError: () => void
  onAddAudio?: (file: File, onAddAttachment: (attachment: Attachment) => void) => void
  onRenameAudio?: (attachment: Attachment) => void
  ttsGroup?: ICreateTextToSpeechStoreProps['group']
  mediaLibraryConfiguration?: Partial<IMediaLibraryStoreProps>
}

export class RinglessMessageFieldStore {
  private _modalId = nanoid()
  private _ttsModalId = nanoid()
  clearParentError: IRinglessMessageFieldStoreProps['clearParentError'] | null
  readonly ttsGroup: ICreateTextToSpeechStoreProps['group'] | null
  readonly onAddAudio: IRinglessMessageFieldStoreProps['onAddAudio'] | null
  readonly onRenameAudioCallback: IRinglessMessageFieldStoreProps['onRenameAudio'] | null
  readonly mediaLibraryConfiguration: Partial<IMediaLibraryStoreProps> | null
  constructor(private _entities: IEntitiesStore, props?: IRinglessMessageFieldStoreProps) {
    makeAutoObservable(this)
    this.clearParentError = props?.clearParentError || null
    this.onAddAudio = props?.onAddAudio || null
    this.onRenameAudioCallback = props?.onRenameAudio || null
    this.ttsGroup = props?.ttsGroup || null
    this.mediaLibraryConfiguration = props?.mediaLibraryConfiguration || null
  }

  loadingAudio = false
  recordAudioStore: RecordAudioStore = new RecordAudioStore({
    AUDIO_RECORD_LIMIT: 180,
    isPauseOnLimit: true,
  })

  attachment: Attachment | null = null

  error: ReactNode = ''

  reset = () => {
    modalStore.removeModal(this._modalId)
    modalStore.removeModal(this._ttsModalId)
  }

  get ringlessMessageDataParams(): IRinglessMessageDataParams {
    return {
      message: '',
      is_rvm: true,
      media_url: this.attachment ? [new RequestAttachment(this.attachment)] : [],
    }
  }

  init = (responseMediaUrl: IResponseMediaShort) => {
    this.attachment = new Attachment({ responseMediaShort: responseMediaUrl })
  }

  private setLoadingAudio = (value: boolean): void => {
    this.loadingAudio = value
  }

  onCancelUploadingAudio = () => {
    this.setLoadingAudio(false)
  }

  private addAttachment = (attachment: Attachment): void => {
    this.attachment = attachment
  }

  private setExtError = (title: string, desc?: string) => {
    toastStore.add({
      type: 'error',
      title,
      desc,
    })
    this.onErrorUploadingAudio()
  }
  private setLimitSizeError = (limitError: ILimitSizeError) => {
    toastStore.add({
      type: 'error',
      ...sizeAlertCreator(limitError),
    })
    this.onErrorUploadingAudio()
  }

  private onErrorUploadingAudio = () => {
    this.setLoadingAudio(false)
    this.attachment = null
  }

  closeRecordModal = () => {
    modalStore.removeModal(this._modalId)
  }

  initRecordAudio = () => {
    const handleFinish = (file: File) => {
      const name = `Recorded audio ${dayjs().format(DayjsFormats.full7)}`
      const audio = new File([file], `${name}.mp3`, {
        type: file.type,
        lastModified: file.lastModified,
      })
      this.closeRecordModal()
      this.onAddFiles([audio])
    }
    void this.recordAudioStore.init({ handleFinish, handleClose: this.closeRecordModal })
  }

  handleRecordAudio = () => {
    this.clearError()
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Record audio',
      ModalContent: () => <RinglessRecordAudioModal store={this} />,
      pureContent: true,
      width: 480,
      onClose: () => this.closeRecordModal(),
    })
  }

  onAddFiles = (files: Array<File>) => {
    this.clearError()
    this.setLoadingAudio(true)
    const file = files.length && files[0]
    if (file) {
      if (file.type.includes('audio')) {
        if (this.onAddAudio) {
          this.onAddAudio(file, (attachment: Attachment) => {
            this.addAttachment(attachment)
            this.setLoadingAudio(false)
          })
        } else {
          this._entities.attachmentStore.addMediaFiles({
            files: [file],
            addAttachment: this.addAttachment,
            setExtError: this.setExtError,
            setLimitSizeError: this.setLimitSizeError,
          })
        }
      } else {
        this.setExtError('The file is not supported for upload.', 'Please upload only mp3')
      }
    }
  }

  onAddAttachmentAudio = (attachment: Attachment): void => {
    this.addAttachment(attachment)
  }

  onAttachmentUploadSuccessful = ({ attachment }: { attachment: IResponseMedia }) => {
    if (attachment.id === this.attachment?.id) {
      this.addAttachment(new Attachment({ responseMedia: attachment }))
      this.setLoadingAudio(false)
    }
  }

  onAttachmentUploadError = (data: IResponseEventAttachmentUploadError) => {
    if (data.attachment.id === this.attachment?.id) {
      this.onErrorUploadingAudio()
    }
  }

  onRemoveAudio = () => {
    this.attachment = null
  }

  private resolveAttachmentId = async (): Promise<Attachment | null> => {
    const attachment = this.attachment
    if (!attachment) return null
    if (typeof attachment.id === 'number') return attachment

    const url = attachment.source || attachment.id
    if (!url) return null

    try {
      const { data } = await AttachmentsApi.getAttachmentByUrl(url, AttachmentFetchType.Audio)
      const audioList = data?.attachments?.audio ?? []
      const media = audioList.find((item) => item.source === url) ?? audioList[0]
      if (!media) return null
      runInAction(() => {
        if (this.attachment && this.attachment.source === url) {
          this.attachment = new Attachment({ responseMedia: media })
        }
      })
      return this.attachment && typeof this.attachment.id === 'number' ? this.attachment : null
    } catch (e) {
      logger.error(e)
      return null
    }
  }

  onRenameAudio = async () => {
    if (!this.attachment) return

    if (this.onRenameAudioCallback) {
      this.onRenameAudioCallback(this.attachment)
      return
    }

    const resolved = await this.resolveAttachmentId()
    if (!resolved || typeof resolved.id !== 'number') {
      toastStore.add({
        type: 'error',
        title: 'Unable to rename this audio',
      })
      return
    }

    const attachmentId = resolved.id

    new RenameModalStore({
      title: 'Rename',
      name: resolved.name || '',
      withPostfix: true,
      element: 'File',
      isEditExtensionDisabled: true,
      isRenamedToast: true,
      onSave: async (name: string) => {
        const { data } = await AttachmentsApi.rename(attachmentId, name)
        runInAction(() => {
          if (this.attachment && this.attachment.id === attachmentId && data) {
            this.attachment = new Attachment({ responseMedia: data })
          }
        })
      },
    })
  }

  handleTextToSpeech = () => {
    this.clearError()
    const onClose = () => modalStore.removeModal(this._ttsModalId)
    const onCreateSpeech = (file: File) => {
      this.onAddFiles([file])
    }
    const ttsStore = new TextToSpeechWidgetStore(
      {
        type: 'create',
        onClose,
        onCreateSpeech,
        group: this.ttsGroup || 'broadcast',
        autoName: true,
      },
      this._entities
    )
    modalStore.addModal({
      id: this._ttsModalId,
      type: ModalTypeList.DEFAULT,
      title: 'Text to speech',
      ModalContent: () => <TextToSpeechModalContent store={ttsStore} onClose={onClose} />,
      pureContent: true,
      width: 480,
      onClose,
    })
  }

  setError = (error: ReactNode) => {
    this.error = error
  }
  clearError = () => {
    this.setError('')
    this.clearParentError?.()
  }
}
